import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Users, Building2, Plus, Pencil, Trash2, Save, X, Loader2, ChevronDown, ChevronRight } from "lucide-react";

export default function AdminClientGroups() {
  const [groups, setGroups] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newForm, setNewForm] = useState({ name: "", description: "", company_ids: new Set() });
  const [editing, setEditing] = useState(null); // group object being edited
  const [editForm, setEditForm] = useState({ name: "", description: "", company_ids: new Set() });
  const [expandedId, setExpandedId] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: g }, { data: c }] = await Promise.all([
        api.get("/client-groups"),
        api.get("/companies"),
      ]);
      setGroups(Array.isArray(g) ? g : []);
      setCompanies(Array.isArray(c) ? c : []);
    } catch (e) {
      toast.error("Errore caricamento gruppi");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Which companies are already in another group → shown as "disabled" during creation/edit
  const usedElsewhere = useMemo(() => {
    const map = new Map();
    for (const g of groups) {
      for (const cid of g.company_ids || []) {
        map.set(cid, g);
      }
    }
    return map;
  }, [groups]);

  const isCompanyBlocked = (cid, currentGroupId = null) => {
    const owner = usedElsewhere.get(cid);
    if (!owner) return null;
    if (currentGroupId && owner.id === currentGroupId) return null;
    return owner.name;
  };

  const toggleCompanyIn = (setState, cid) => {
    setState((prev) => {
      const s = new Set(prev.company_ids);
      if (s.has(cid)) s.delete(cid);
      else s.add(cid);
      return { ...prev, company_ids: s };
    });
  };

  const createGroup = async () => {
    if (!newForm.name.trim()) return toast.error("Nome gruppo obbligatorio");
    setBusy(true);
    try {
      await api.post("/client-groups", {
        name: newForm.name.trim(),
        description: newForm.description.trim() || null,
        company_ids: Array.from(newForm.company_ids),
      });
      toast.success("Gruppo creato");
      setCreating(false);
      setNewForm({ name: "", description: "", company_ids: new Set() });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore creazione");
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (g) => {
    setEditing(g);
    setEditForm({
      name: g.name,
      description: g.description || "",
      company_ids: new Set(g.company_ids || []),
    });
  };
  const closeEdit = () => setEditing(null);

  const saveEdit = async () => {
    if (!editForm.name.trim()) return toast.error("Nome obbligatorio");
    setBusy(true);
    try {
      await api.patch(`/client-groups/${editing.id}`, {
        name: editForm.name.trim(),
        description: editForm.description.trim() || null,
        company_ids: Array.from(editForm.company_ids),
      });
      toast.success("Gruppo aggiornato");
      closeEdit();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore aggiornamento");
    } finally {
      setBusy(false);
    }
  };

  const del = (g) => {
    setConfirmState({
      title: `Eliminare il gruppo "${g.name}"?`,
      message: "Le aziende torneranno disponibili per altri gruppi. Il gruppo non è più selezionabile in fase di pubblicazione.",
      confirmLabel: "Elimina",
      variant: "danger",
      onConfirm: async () => {
        try {
          await api.delete(`/client-groups/${g.id}`);
          toast.success("Gruppo eliminato");
          load();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Errore eliminazione");
        }
      },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Gruppi Clienti</h1>
          <p className="text-sm text-slate-500 mt-1">
            Raggruppa aziende clienti riutilizzabili come target di News/Report/Report Mercati Energetici.
            Ogni azienda può stare in <b>un solo</b> gruppo.
          </p>
        </div>
        {!creating && (
          <button
            onClick={() => setCreating(true)}
            className="btn-brand flex items-center gap-2"
            data-testid="cg-new-btn"
          >
            <Plus size={16} /> Nuovo gruppo
          </button>
        )}
      </div>

      {creating && (
        <div className="card-elevated p-5 space-y-3" data-testid="cg-new-form">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-semibold text-slate-900 flex items-center gap-2">
              <Users size={16} className="text-blue-600" /> Nuovo gruppo
            </h3>
            <button onClick={() => setCreating(false)} className="text-slate-400 hover:text-slate-600 p-1">
              <X size={16} />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-600 mb-1">Nome</label>
              <input
                value={newForm.name}
                onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
                placeholder="Es. Grandi Consumatori Gas"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
                data-testid="cg-new-name"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-600 mb-1">Descrizione</label>
              <input
                value={newForm.description}
                onChange={(e) => setNewForm({ ...newForm, description: e.target.value })}
                placeholder="Opzionale"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
                data-testid="cg-new-desc"
              />
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-600 mb-1">Aziende del gruppo</label>
            <CompanyPicker
              companies={companies}
              selected={newForm.company_ids}
              onToggle={(cid) => toggleCompanyIn(setNewForm, cid)}
              blockedLabel={(cid) => isCompanyBlocked(cid)}
              testid="cg-new-companies"
            />
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              onClick={() => setCreating(false)}
              className="px-3 py-1.5 text-sm rounded-lg bg-white border border-slate-200 hover:bg-slate-100"
              data-testid="cg-new-cancel"
            >
              Annulla
            </button>
            <button
              onClick={createGroup}
              disabled={busy || !newForm.name.trim()}
              className="btn-brand flex items-center gap-2 text-sm !py-1.5 disabled:opacity-50"
              data-testid="cg-new-save"
            >
              {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Crea gruppo
            </button>
          </div>
        </div>
      )}

      <div className="card-elevated overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 font-display font-semibold text-slate-700 flex items-center gap-2">
          <Users size={16} className="text-blue-600" />
          Gruppi attivi
          <span className="text-xs font-normal text-slate-400">({groups.length})</span>
        </div>
        {loading ? (
          <div className="p-8 flex items-center justify-center text-slate-500 gap-2 text-sm">
            <Loader2 className="animate-spin" size={14} /> Caricamento…
          </div>
        ) : groups.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            Nessun gruppo creato. Crea il primo per raggruppare aziende con esigenze simili.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {groups.map((g) => (
              <div key={g.id} data-testid={`cg-row-${g.id}`}>
                <div className="px-5 py-3 flex items-center gap-3 hover:bg-slate-50/60">
                  <button
                    onClick={() => setExpandedId(expandedId === g.id ? null : g.id)}
                    className="text-slate-400 hover:text-slate-700"
                    data-testid={`cg-toggle-${g.id}`}
                  >
                    {expandedId === g.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{g.name}</div>
                    {g.description && (
                      <div className="text-xs text-slate-500 truncate">{g.description}</div>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 whitespace-nowrap">
                    {(g.companies || []).length} aziend{(g.companies || []).length === 1 ? "a" : "e"} · {g.users_total || 0} utent{g.users_total === 1 ? "e" : "i"}
                  </div>
                  <button
                    onClick={() => openEdit(g)}
                    className="text-slate-500 hover:text-slate-800 p-1"
                    data-testid={`cg-edit-${g.id}`}
                    title="Modifica"
                  >
                    <Pencil size={15} />
                  </button>
                  <button
                    onClick={() => del(g)}
                    className="text-red-500 hover:text-red-700 p-1"
                    data-testid={`cg-delete-${g.id}`}
                    title="Elimina"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
                {expandedId === g.id && (
                  <div className="px-8 py-3 bg-slate-50/40 border-t border-slate-100" data-testid={`cg-detail-${g.id}`}>
                    {(g.companies || []).length === 0 ? (
                      <div className="text-xs text-slate-500 italic">Nessuna azienda assegnata.</div>
                    ) : (
                      <div className="space-y-3">
                        {g.companies.map((c) => (
                          <div key={c.id}>
                            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5 mb-1">
                              <Building2 size={12} /> {c.name}
                              <span className="text-slate-400 font-normal normal-case tracking-normal">({c.users.length})</span>
                            </div>
                            {c.users.length === 0 ? (
                              <div className="text-[11px] text-slate-400 italic pl-4">Nessun utente attivo.</div>
                            ) : (
                              <ul className="pl-4 space-y-0.5">
                                {c.users.map((u) => (
                                  <li key={u.id} className="text-xs text-slate-700 flex items-center gap-2">
                                    <span>{u.full_name || "—"}</span>
                                    <span className="text-slate-400">·</span>
                                    <span className="text-slate-500">{u.email}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeEdit} data-testid="cg-edit-modal">
          <div className="bg-white rounded-xl shadow-xl max-w-xl w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl font-semibold text-slate-900">Modifica gruppo</h3>
              <button onClick={closeEdit} className="text-slate-400 hover:text-slate-600 p-1"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">Nome</label>
                <input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
                  data-testid="cg-edit-name"
                />
              </div>
              <div>
                <label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">Descrizione</label>
                <input
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
                  data-testid="cg-edit-desc"
                />
              </div>
              <div>
                <label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">Aziende del gruppo</label>
                <CompanyPicker
                  companies={companies}
                  selected={editForm.company_ids}
                  onToggle={(cid) => toggleCompanyIn(setEditForm, cid)}
                  blockedLabel={(cid) => isCompanyBlocked(cid, editing.id)}
                  testid="cg-edit-companies"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-4">
              <button onClick={closeEdit} className="px-3 py-1.5 text-sm rounded-lg bg-white border border-slate-200 hover:bg-slate-100" data-testid="cg-edit-cancel">
                Annulla
              </button>
              <button
                onClick={saveEdit}
                disabled={busy || !editForm.name.trim()}
                className="btn-brand flex items-center gap-2 text-sm !py-1.5 disabled:opacity-50"
                data-testid="cg-edit-save"
              >
                {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Salva
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </div>
  );
}

function CompanyPicker({ companies, selected, onToggle, blockedLabel, testid }) {
  return (
    <div className="mt-1 max-h-64 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid={testid}>
      {companies.length === 0 ? (
        <div className="p-3 text-xs text-slate-500 text-center">Nessuna azienda disponibile.</div>
      ) : (
        companies.map((c) => {
          const blocked = blockedLabel(c.id);
          const disabled = !!blocked && !selected.has(c.id);
          return (
            <label
              key={c.id}
              className={`flex items-center gap-3 px-3 py-2 text-sm ${
                disabled ? "opacity-50 cursor-not-allowed bg-slate-50" : "cursor-pointer hover:bg-slate-50"
              }`}
              data-testid={`${testid}-item-${c.id}`}
            >
              <input
                type="checkbox"
                checked={selected.has(c.id)}
                disabled={disabled}
                onChange={() => onToggle(c.id)}
              />
              <span className="flex-1 text-slate-800">{c.name}</span>
              {blocked && (
                <span className="text-[10px] text-amber-600 font-medium">già in "{blocked}"</span>
              )}
            </label>
          );
        })
      )}
    </div>
  );
}
